import { ReactElement, ReactNode, useEffect, useMemo, useRef } from 'react'
import { CameraControllerV2, TapelineV2 } from '@scene'
import {
  Vector3,
  Object3D,
  LinearSRGBColorSpace,
  Color,
  Intersection,
  Scene as ThreeScene,
} from 'three'
import { useTheme, ThemeProvider } from '@mui/material/styles'
import { ModuGenScene, useCameraStore } from '@modugen/ui-toolkit'
import { events as createPointerEvents, RootState } from '@react-three/fiber'
import { useControlStore } from '@editorStores'

interface SceneProps {
  children: ReactNode
  /**
   * Stops rendering frames, e.g. while an overlay (the plan view's PDF preview) covers the scene,
   * which would otherwise keep rendering underneath and compete with the overlay's work. Set via the
   * Canvas `frameloop` prop: the Canvas re-applies it on every render, so it can't be overridden.
   */
  paused?: boolean
}

const CLIP_RAYCAST_EPSILON = 1e-5

const Scene = ({ children, paused = false }: SceneProps): ReactElement => {
  const theme = useTheme()
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const sceneRef = useRef<ThreeScene | null>(null)
  const screenshotToolElement = useControlStore(state => state.screenshotToolElement)

  useEffect(() => {
    Object3D.DEFAULT_UP = new Vector3(0, 0, 1)
  }, [])

  // Update background color when orthographic mode changes
  useEffect(() => {
    if (sceneRef.current) {
      const backgroundColor = isOrthographic
        ? theme.scenePalette.background2D
        : theme.scenePalette.background3D
      sceneRef.current.background = new Color(backgroundColor)
    }
  }, [isOrthographic, theme.scenePalette.background2D, theme.scenePalette.background3D])

  const screenshotToolVisible = useMemo(() => {
    return !!screenshotToolElement
  }, [screenshotToolElement])

  const sceneProps = useMemo(
    () => ({
      legacy: true,
      frameloop: paused ? ('never' as const) : ('always' as const),
      onCreated: (state: RootState) => {
        state.gl.outputColorSpace = LinearSRGBColorSpace
        // Store scene reference for dynamic background updates
        sceneRef.current = state.scene
        // Set the scene background color based on view mode
        const backgroundColor = isOrthographic
          ? theme.scenePalette.background2D
          : theme.scenePalette.background3D
        state.scene.background = new Color(backgroundColor)
      },
      gl: {
        preserveDrawingBuffer: screenshotToolVisible,
      },
      events: (state: Parameters<typeof createPointerEvents>[0]) => ({
        ...createPointerEvents(state),
        filter: (intersections: Intersection[], rootState: RootState) => {
          const clippingPlanes = rootState.gl.clippingPlanes

          if (clippingPlanes.length === 0) {
            return intersections
          }

          return intersections.filter(intersection => {
            return clippingPlanes.every(
              plane => plane.distanceToPoint(intersection.point) >= -CLIP_RAYCAST_EPSILON,
            )
          })
        },
      }),
    }),
    [
      paused,
      isOrthographic,
      screenshotToolVisible,
      theme.scenePalette.background2D,
      theme.scenePalette.background3D,
    ],
  )

  return (
    <ModuGenScene
      key={screenshotToolVisible ? 'screenshot' : 'normal'}
      canvasProps={sceneProps}
      cameraController={false}
      tapelineController={false}
    >
      {/* cannot share context with rest of application */}
      <ThemeProvider theme={theme}>
        <ambientLight intensity={1 * Math.PI} />
        <pointLight position={[15, 10, 35]} intensity={0.35 * Math.PI} />
        <pointLight position={[-15, -10, 35]} intensity={0.35 * Math.PI} />

        <CameraControllerV2 />
        <TapelineV2 />

        {children}
      </ThemeProvider>
    </ModuGenScene>
  )
}

export default Scene
